<template>
  <div class="parking-spaces-assignment">
    <v-container grid-list-md>
      <v-layout row wrap>
        <v-flex xs12>
          <h1 class="display-1 font-weight-bold mb-2">Asignar Espacios a Empresas</h1>
          <p class="subtitle grey--text">Gestiona la asignación de espacios de estacionamiento por empresa</p>
        </v-flex>
      </v-layout>

      <!-- Error general -->
      <v-layout row wrap v-if="generalError" class="mb-3">
        <v-flex xs12>
          <v-alert type="error" dismissible :value="true" @input="generalError = ''">
            {{ generalError }}
          </v-alert>
        </v-flex>
      </v-layout>

      <!-- Filtros -->
      <v-layout row wrap class="mb-4">
        <v-flex xs12 sm6>
          <v-select
            v-model="selectedCompany"
            :items="companies"
            item-text="name"
            item-value="id"
            label="Selecciona una empresa"
            prepend-icon="business"
            outlined
            @input="onCompanyChange"
          ></v-select>
        </v-flex>
        <v-flex xs12 sm6>
          <v-select
            v-model="filterStatus"
            :items="statusOptions"
            label="Filtrar por estado"
            prepend-icon="filter_list"
            outlined
          ></v-select>
        </v-flex>
      </v-layout>

      <!-- Información de la empresa seleccionada -->
      <v-layout row wrap v-if="selectedCompanyData" class="mb-4">
        <v-flex xs12>
          <v-card class="elevation-2">
            <v-card-text>
              <v-layout row>
                <v-flex xs12 sm6>
                  <p><strong>Empresa:</strong> {{ selectedCompanyData.name }}</p>
                  <p><strong>Espacios Asignados:</strong> {{ assignedSpacesCount }}</p>
                </v-flex>
                <v-flex xs12 sm6>
                  <p><strong>Contacto:</strong> {{ selectedCompanyData.contact || '-' }}</p>
                  <p><strong>Teléfono:</strong> {{ selectedCompanyData.phone || '-' }}</p>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Tabla de espacios disponibles y asignados -->
      <v-layout row wrap v-if="selectedCompany">
        <v-flex xs12>
          <v-card class="elevation-2">
            <v-card-title>Espacios de Estacionamiento</v-card-title>
            <v-data-table
              :headers="headers"
              :items="filteredSpacesList"
              :loading="loadingSpaces"
              class="elevation-1"
              :no-data-text="'No hay espacios disponibles'"
              :rows-per-page-items="[10, 20, 50]"
              :rows-per-page="10"
            >
              <template v-slot:items="props">
                <td class="text-xs-center">{{ props.item.code }}</td>
                <td class="text-xs-center">{{ props.item.type }}</td>
                <td class="text-xs-center">
                  <v-chip :color="getStatusColor(props.item.status)" text-color="white" small>
                    {{ props.item.status }}
                  </v-chip>
                </td>
                <td class="text-xs-center">
                  <v-switch
                    v-if="!props.item.assignedToOther"
                    :input-value="props.item.assigned"
                    :loading="savingId === props.item.id"
                    :disabled="savingId === props.item.id"
                    @change="val => toggleAssignment(props.item, val)"
                    class="mt-0"
                  ></v-switch>
                  <span v-else class="grey--text">Asignado a otro</span>
                </td>
                <td class="text-xs-center">
                  <v-btn
                    v-if="props.item.assigned"
                    small
                    color="error"
                    :loading="savingId === props.item.id"
                    @click="unassignSpace(props.item)"
                  >
                    Desasignar
                  </v-btn>
                </td>
              </template>
              <template v-slot:no-results>
                <v-alert type="info" :value="true">No hay espacios para mostrar</v-alert>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Resumen de asignaciones -->
      <v-layout row wrap class="mt-4" v-if="selectedCompany">
        <v-flex xs12 sm4>
          <v-card class="elevation-1 text-xs-center pa-4">
            <v-icon large color="success">check_circle</v-icon>
            <h3 class="title">{{ assignedSpacesCount }}</h3>
            <p class="grey--text">Asignados</p>
          </v-card>
        </v-flex>
        <v-flex xs12 sm4>
          <v-card class="elevation-1 text-xs-center pa-4">
            <v-icon large color="warning">info</v-icon>
            <h3 class="title">{{ availableSpacesCount }}</h3>
            <p class="grey--text">Disponibles</p>
          </v-card>
        </v-flex>
        <v-flex xs12 sm4>
          <v-card class="elevation-1 text-xs-center pa-4">
            <v-icon large color="error">block</v-icon>
            <h3 class="title">{{ blockedSpacesCount }}</h3>
            <p class="grey--text">Bloqueados</p>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import '@/plugins/axios'
import axios from 'axios'
import store from '@/store'

const API_BASE_URL = 'https://empresa-service.intrare.app'

export default {
  name: 'ParkingSpacesAssignment',
  data() {
    return {
      loadingSpaces: false,
      savingId: null,
      generalError: '',
      selectedCompany: null,
      filterStatus: null,

      headers: [
        { text: 'Código', value: 'code', align: 'center', width: '120px' },
        { text: 'Tipo', value: 'type', align: 'center', width: '120px' },
        { text: 'Estado', value: 'status', align: 'center', width: '150px' },
        { text: 'Asignar', value: 'assigned', align: 'center', width: '100px', sortable: false },
        { text: 'Acciones', value: 'actions', align: 'center', sortable: false, width: '120px' }
      ],

      statusOptions: [
        'todos',
        'disponible',
        'ocupado',
        'bloqueado',
        'mantenimiento'
      ],

      companies: [],
      allSpaces: []
    }
  },

  computed: {
    selectedCompanyData() {
      return this.companies.find(c => c.id === this.selectedCompany)
    },

    filteredSpacesList() {
      let spaces = this.allSpaces.map(s => ({
        ...s,
        assigned: s.company === this.selectedCompany,
        assignedToOther: s.company !== null && s.company !== this.selectedCompany
      }))

      if (this.filterStatus && this.filterStatus !== 'todos') {
        spaces = spaces.filter(s => s.status === this.filterStatus)
      }

      return spaces
    },

    assignedSpacesCount() {
      if (!this.selectedCompany) return 0
      return this.allSpaces.filter(s => s.company === this.selectedCompany).length
    },

    availableSpacesCount() {
      return this.allSpaces.filter(s => s.status === 'disponible' && (s.company === null || s.company === this.selectedCompany)).length
    },

    blockedSpacesCount() {
      return this.allSpaces.filter(s => s.status === 'bloqueado').length
    }
  },

  mounted() {
    this.fetchCompanies()
    this.fetchSpaces()
  },

  methods: {
    fetchCompanies() {
      const config = {
        method: 'get',
        url: `${API_BASE_URL}/parques/empresas`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      axios(config)
        .then(response => {
          this.companies = response.data
        })
        .catch(error => {
          this.generalError = (error.response && error.response.data && error.response.data.error) || 'Error al cargar las empresas'
        })
    },

    fetchSpaces() {
      this.loadingSpaces = true
      const config = {
        method: 'get',
        url: `${API_BASE_URL}/Parking/building-spaces/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      axios(config)
        .then(response => {
          this.allSpaces = response.data
        })
        .catch(error => {
          this.generalError = (error.response && error.response.data && error.response.data.error) || 'Error al cargar los espacios de estacionamiento'
        })
        .finally(() => {
          this.loadingSpaces = false
        })
    },

    onCompanyChange() {
      this.filterStatus = null
    },

    toggleAssignment(space, isAssigned) {
      const companyId = isAssigned ? this.selectedCompany : null
      this.patchSpaceCompany(space, companyId)
    },

    unassignSpace(space) {
      this.patchSpaceCompany(space, null)
    },

    patchSpaceCompany(space, companyId) {
      this.savingId = space.id
      const config = {
        method: 'patch',
        url: `${API_BASE_URL}/Parking/building-spaces/${space.id}/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        },
        data: { company: companyId }
      }
      axios(config)
        .then(response => {
          const idx = this.allSpaces.findIndex(s => s.id === space.id)
          if (idx !== -1) {
            this.allSpaces.splice(idx, 1, response.data)
          }
          const message = companyId
            ? `Espacio ${space.code} asignado correctamente`
            : `Espacio ${space.code} desasignado`
          this.$store.commit('snackbar_change_message', message)
          this.$store.commit('snackbar_activate_true')
        })
        .catch(error => {
          this.generalError = (error.response && error.response.data && error.response.data.error) || 'Error al actualizar la asignación del espacio'
        })
        .finally(() => {
          this.savingId = null
        })
    },

    getStatusColor(status) {
      const colors = {
        disponible: 'success',
        ocupado: 'warning',
        bloqueado: 'error',
        mantenimiento: 'info'
      }
      return colors[status] || 'grey'
    }
  }
}
</script>

<style scoped>
.parking-spaces-assignment {
  padding: 20px;
}
</style>
